import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANGUAGES, LANGUAGE_MAP, DEFAULT_LANGUAGE, STORAGE_KEYS, detectScope } from "@/i18n/config";

const LanguageContext = createContext(null);

function readStored(scope) {
  try {
    const v = localStorage.getItem(STORAGE_KEYS[scope]);
    return v && LANGUAGE_MAP[v] ? v : null;
  } catch {
    return null;
  }
}

function browserDefault() {
  try {
    const raw = (navigator.language || "").toLowerCase().split("-")[0];
    return LANGUAGE_MAP[raw] ? raw : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

export function LanguageProvider({ children }) {
  const { i18n } = useTranslation();
  const location = useLocation();
  const scope = detectScope(location.pathname);

  const [customerLang, setCustomerLang] = useState(() => {
    const stored = readStored("customer");
    if (stored) return stored;
    const detected = browserDefault();
    // Persist auto-detected value so the choice sticks across sessions.
    try { localStorage.setItem(STORAGE_KEYS.customer, detected); } catch {}
    return detected;
  });
  const [ownerLang, setOwnerLang] = useState(() => {
    const stored = readStored("owner");
    if (stored) return stored;
    const detected = browserDefault();
    try { localStorage.setItem(STORAGE_KEYS.owner, detected); } catch {}
    return detected;
  });

  const activeLang = scope === "owner" ? ownerLang : customerLang;

  // Sync i18next + <html lang/dir> whenever the active language changes.
  useEffect(() => {
    const meta = LANGUAGE_MAP[activeLang] || LANGUAGE_MAP[DEFAULT_LANGUAGE];
    i18n.changeLanguage(activeLang);
    document.documentElement.lang = activeLang;
    document.documentElement.dir = meta.dir;
  }, [activeLang, i18n]);

  const setLanguage = useCallback((code) => {
    if (!LANGUAGE_MAP[code]) return;
    try { localStorage.setItem(STORAGE_KEYS[scope], code); } catch {}
    if (scope === "owner") setOwnerLang(code); else setCustomerLang(code);
  }, [scope]);

  const value = useMemo(() => ({
    scope,
    language: activeLang,
    meta: LANGUAGE_MAP[activeLang] || LANGUAGE_MAP[DEFAULT_LANGUAGE],
    languages: LANGUAGES,
    setLanguage,
  }), [scope, activeLang, setLanguage]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside <LanguageProvider>");
  return ctx;
}
